import { kvsSessionDurationM } from '@/globals/constants';
import dayjs from 'dayjs';
import { getDayjsFromSecomDate } from './date';
import {
  CameraFirmwareType,
  CLIP_NAME_ALLOWED_REGEX,
  INPUT_WHITESPACE_REGEX,
  PlayerStatus,
} from '@/globals/enums';
import debounce from 'lodash/debounce';
import axios from 'axios';

/**
 * Creates an abortable version of a Promise-returning function.
 *
 * @param {Function} fn - The original function that returns a Promise.
 * @returns {Function} A new function that accepts an AbortSignal as its last argument.
 */
export function makeAbortable(fn) {
  return function (...args) {
    const lastArg = args[args.length - 1];
    const hasSignal = lastArg instanceof AbortSignal;
    const signal = hasSignal ? args.pop() : null;

    return new Promise((resolve, reject) => {
      if (signal?.aborted) {
        reject(new DOMException('Aborted', 'AbortError'));
        return;
      }

      const abortHandler = () => {
        reject(new DOMException('Aborted', 'AbortError'));
      };

      if (signal) {
        signal.addEventListener('abort', abortHandler);
      }

      fn(...args)
        .then(resolve)
        .catch(reject)
        .finally(() => {
          if (signal) {
            signal.removeEventListener('abort', abortHandler);
          }
        });
    });
  };
}

/**
 * Calculate new height for correct ratio for given canvas width
 * @param {*} canvasWidth
 * @param {*} originalWidth
 * @param {*} originalHeight
 * @param {*} ratio
 * @param {*} tolerance
 * @returns
 */
export const calculateNewHeight = (
  canvasWidth,
  originalWidth,
  originalHeight,
  ratio = 1,
  tolerance = 0
) => {
  if (!originalWidth) {
    return 0;
  }
  const widthDifference = Math.abs(canvasWidth - originalWidth);
  if (widthDifference <= tolerance) {
    return originalHeight; // return original height if width difference is within tolerance
  }

  return Math.round((canvasWidth * originalHeight * ratio) / originalWidth);
};

export const naiveRound = (num, decimalPlaces = 0) => {
  var p = Math.pow(10, decimalPlaces);
  return Math.round(num * p) / p;
};

export const urlBase64ToUint8Array = (base64String) => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    // eslint-disable-next-line no-useless-escape
    .replace(/\-/g, '+')
    .replace(/_/g, '/');

  const rawData = atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
};

/**
 * Format date/time to YYYYMMDDhhmmss for clip default name.
 * @param {Date|number|string} value - Unix ms, Date, or YYYYMMDDHHmmss string
 * @returns {string}
 */
const formatClipDateTime = (value) => {
  if (typeof value === 'string' && value.length === 14 && /^\d+$/.test(value)) {
    return value;
  }
  return dayjs(value).format('YYYYMMDDHHmmss');
};

/**
 * Default clip name: XXX_YYYYMMDDhhmmss_YYYYMMDDhhmmss (camera, start, end).
 * @param {string} cameraName
 * @param {Date|number|string} startTime - Unix ms, Date, or YYYYMMDDHHmmss
 * @param {Date|number|string} endTime - Unix ms, Date, or YYYYMMDDHHmmss
 * @returns {string}
 */
export const getVideoClipName = (cameraName, startTime, endTime) => {
  const startStr = formatClipDateTime(startTime);
  const endStr = formatClipDateTime(endTime);
  return `${cameraName}_${startStr}_${endStr}`;
};

/**
 * Return pagination data in Array
 * @param {*} arr
 * @param {*} size
 * @returns
 */
export const paginate = (arr, size) => {
  return arr.reduce((acc, val, i) => {
    let idx = Math.floor(i / size);
    let page = acc[idx] || (acc[idx] = []);
    page.push(val);

    return acc;
  }, []);
};

/**
 * Slice array by pagination
 * @param {*} array
 * @param {*} page_size
 * @param {*} page_number
 * @returns
 */
export const sliceArrayByPage = (array, page_size, page_number) => {
  // human-readable page numbers usually start with 1, so we reduce 1 in the first argument
  return array.slice((page_number - 1) * page_size, page_number * page_size);
};

/**
 * Preload image
 * @param {*} src
 * @returns
 */
export const preloadImage = (src) =>
  new Promise((resolve, reject) => {
    const image = new Image();

    image.onload = () => {
      resolve(image);
    };
    image.onerror = reject;
    image.setAttribute('crossOrigin', 'anonymous');
    image.src = src;
  });

/**
 * Get random number
 * @returns {String}
 */
export const getRandomId = () => {
  return Math.random().toString(36).substring(2).toUpperCase();
};

/**
 * Resize image and convert to data URL
 * @param {Image} img
 * @returns
 */
export const resizeImage = (img) => {
  const c = document.createElement('canvas');
  const canvasWidth = 400;
  const newHeight = calculateNewHeight(
    canvasWidth,
    img.naturalWidth,
    img.naturalHeight
  );
  if (!newHeight) {
    throw new Error('Calculated new height is not expected!');
  }
  c.height = newHeight;
  c.width = canvasWidth;

  const ctx = c.getContext('2d');

  ctx.drawImage(img, 0, 0, c.width, c.height);
  return c.toDataURL();
};

/**
 * Group array by key
 * @param {Array<any>} arr
 * @param {String} key
 * @returns
 */
export const groupBy = (arr, key) => {
  return arr.reduce((rv, x) => {
    (rv[x[key]] = rv[x[key]] || []).push(x);
    return rv;
  }, {});
};

// Get event style
export const getEvenLabelStyle = (label) => {
  const style = {};
  if (!label) {
    return null;
  }
  if (label.trim().startsWith('ライン通過検知')) {
    style.color = '#4b4b4b';
    style.icon = `/img/events/tag_event_1.svg`;
    style.bgColor = '#a5c0e4';
    style.fontSize = '12px';
    style.text = label;
    style.borderColor = null;
    return style;
  }

  switch (label.trim()) {
    case '持ち去り検知':
      style.color = '#4b4b4b';
      style.bgColor = '#d8aae1';
      style.fontSize = '12px';
      style.text = '持ち去り検知';
      style.borderColor = null;
      break;
    case '動体検知':
      style.color = '#4b4b4b';
      style.icon = `/img/events/tag_event_5.svg`;
      style.bgColor = '#efadad';
      style.fontSize = '12px';
      style.text = '動体検知';
      style.borderColor = null;
      break;
    case 'エリア立入検知':
      style.color = '#4b4b4b';
      style.icon = `/img/events/tag_event_2.svg`;
      style.bgColor = '#d8aae1';
      style.fontSize = '12px';
      style.text = 'エリア立入検知';
      style.borderColor = null;
      break;
    case '滞留検知':
      style.color = '#4b4b4b';
      style.icon = `/img/events/tag_event_3.svg`;
      style.bgColor = '#9ad192';
      style.fontSize = '12px';
      style.text = '滞留検知';
      style.borderColor = null;
      break;
    case '混雑検知':
      style.color = '#4b4b4b';
      style.icon = `/img/events/tag_event_4.svg`;
      style.bgColor = '#e3d970';
      style.fontSize = '12px';
      style.text = '混雑検知';
      style.borderColor = null;
      break;
    // case 'ライン通過検知':
    //   style.color = '#4b4b4b';
    //   style.bgColor = '#9ad192';
    //   style.fontSize = '12px';
    //   style.text = 'ライン通過検知';
    //   style.borderColor = null;
    //   break;
    // case 'ライン通過検知（IN）検知':
    //   style.color = '#4b4b4b';
    //   style.bgColor = '#9ad192';
    //   style.fontSize = '12px';
    //   style.text = 'ライン通過検知（IN）検知';
    //   style.borderColor = null;
    //   break;
    case '置き去り検知':
      style.color = '#4b4b4b';
      style.bgColor = '#c4d57e';
      style.fontSize = '12px';
      style.text = '置き去り検知';
      style.borderColor = null;
      break;
    case '顔検知':
      style.color = '#4b4b4b';
      style.bgColor = '#e3d970';
      style.fontSize = '12px';
      style.text = '顔検知';
      style.borderColor = null;
      break;
    case '駆け出し検知':
      style.color = '#4b4b4b';
      style.bgColor = '#efadad';
      style.fontSize = '12px';
      style.text = '駆け出し検知';
      style.borderColor = null;
      break;
    case '不審な動き':
      style.color = '#4b4b4b';
      style.bgColor = '#eb9fb7';
      style.fontSize = '12px';
      style.text = '不審な動き';
      style.borderColor = null;
      break;

    default:
      style.color = '#4b4b4b';
      style.bgColor = '#e3d970';
      style.fontSize = '12px';
      style.text = label;
      style.borderColor = null;
      break;
  }
  return style;
};

/**
 * Get trim [start,end] points
 * @param {*} start
 * @param {*} end
 * @param {*} parentWidth
 * @param {*} rangeStart
 * @returns [Timestamp,Timestamp]
 */
export const getTrimPoints = (
  start,
  end,
  durationFactor,
  parentWidth,
  rangeStart
) => {
  const startPixelInSeconds = (start * durationFactor * 60 * 60) / parentWidth;
  const endPixelInSeconds = (end * durationFactor * 60 * 60) / parentWidth;
  const startTime = dayjs(rangeStart)
    .add(startPixelInSeconds, 'seconds')
    .valueOf();
  const endTime = dayjs(rangeStart).add(endPixelInSeconds, 'seconds').valueOf();
  return [startTime, endTime];
};

/**
 * Covert date/time to pixel
 */
export const convertDateToPixel = (
  datetime,
  startRange,
  endRange,
  parentWidth
) => {
  const rangeDuration = dayjs
    .duration(dayjs(endRange).diff(startRange))
    .asMilliseconds();
  const pointTimeDuration = dayjs
    .duration(dayjs(datetime).diff(startRange))
    .asMilliseconds();
  const oneMsInPixel = parentWidth / rangeDuration;
  return oneMsInPixel * pointTimeDuration;
};

/**
 * Check if the time slot is more that 20 hours or not to fit KVS condition (time slot should be less than 24 hours)
 */
export const checkHLSEndTimestamp = (startTimestamp, endTimestamp) => {
  let currentEndTimestamp = endTimestamp;
  const addEndTimestamp = kvsSessionDurationM;
  const addEndTimestampUnit = 'minutes'; //'hours'

  currentEndTimestamp = dayjs(startTimestamp)
    .add(addEndTimestamp, addEndTimestampUnit)
    .valueOf();

  return currentEndTimestamp;
};

// Calculate current time for a specific sequence number
export const getCurrentTimeForSequenceNumber = (sequenceNumber, segments) => {
  let currentTime = 0;

  // Calculate current time based on segment durations and sequence numbers
  for (let i = 0; i < sequenceNumber - 1 && i < segments.length; i++) {
    currentTime += segments[i].duration;
  }
  return currentTime;
};

export const seekStartPosition = 60;

export const uuidv4 = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0;
    const v = c == 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

export const processRequest = (url) => {
  const textRequestId = url.match(/\[\[reqId:[A-Za-z0-9]+]]/i);
  if (textRequestId) {
    return {
      reqId: textRequestId[0].split(':')[1].split(']')[0],
      url: url.replace(/\[\[reqId:[A-Za-z0-9]+]]/i, ''),
    };
  }

  return { reqId: null, url };
};

export function randomInteger(pMin = 1, pMax = 1000) {
  pMin = Math.round(pMin);
  pMax = Math.round(pMax);
  if (pMax < pMin) {
    let t = pMin;
    pMin = pMax;
    pMax = t;
  }
  return Math.floor(Math.random() * (pMax + 1 - pMin) + pMin);
}

export const bytesToGB = (bytesValue) => {
  return `${(bytesValue / (1000 * 1000)).toFixed(2)} GB`;
};

export const calculatePolygans = ({
  videoMetaWidth,
  videoMetaHeight,
  top,
  left,
  bottom,
  right,
}) => {
  const widthRatio = 9999 / videoMetaWidth; // 9999 / A
  const heightRatio = 9999 / videoMetaHeight; // 9999 / B

  // Convert to pixels
  const leftInPixel = (left * videoMetaWidth) / 100;
  const rightInPixel = (right * videoMetaWidth) / 100;
  const topInPixel = (top * videoMetaHeight) / 100;
  const bottomInPixel = (bottom * videoMetaHeight) / 100;
  const pixelOneWidth = (100 * videoMetaWidth) / 100;
  const pixelOneHeight = (100 * videoMetaHeight) / 100;

  // (a1, b1) top-left | a1 = a4 (leftBottomX), b1 = b2 (rightTopY)
  const x1 = Math.round(leftInPixel * widthRatio);
  const y1 = Math.round(topInPixel * heightRatio);
  // (a2, b2) top-right | rightTopX, rightTopY
  const x2 = Math.round((pixelOneWidth - rightInPixel) * widthRatio);
  const y2 = Math.round(topInPixel * heightRatio);
  // (a3, b3) bottom-right | a3 = a2 (rightTopX), b3 = b4 (leftBottomY)
  const x3 = Math.round((pixelOneWidth - rightInPixel) * widthRatio);
  const y3 = Math.round((pixelOneHeight - bottomInPixel) * heightRatio);
  // (a4, b4) bottom-left | leftBottomX, leftBottomY
  const x4 = Math.round(leftInPixel * widthRatio);
  const y4 = Math.round((pixelOneHeight - bottomInPixel) * heightRatio);

  const polygonstd = [x1, y1, x2, y2, x3, y3, x4, y4];
  const coordinates = {
    top: top,
    left: left,
    right: right,
    bottom: bottom,
  };
  return { polygonstd, coordinates };
};

export const getVideoDurationFromURL = (url) => {
  const video = document.createElement('video');
  video.preload = 'metadata';

  const onLoadedMetaDataPromise = new Promise((resolve) => {
    video.onloadedmetadata = () => {
      resolve(video.duration);
      video.remove();
    };
  });

  video.src = url;

  return onLoadedMetaDataPromise;
};

export const makeRequest = (url, opts) => {
  return new Promise(function (resolve, reject) {
    var xhr = new XMLHttpRequest();
    xhr.open(opts.method, url);
    xhr.onload = function () {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(xhr.response);
      } else {
        reject({
          status: xhr.status,
          statusText: xhr.statusText,
        });
      }
    };
    xhr.onerror = function () {
      reject({
        status: xhr.status,
        statusText: xhr.statusText,
      });
    };
    if (opts.headers) {
      Object.keys(opts.headers).forEach(function (key) {
        xhr.setRequestHeader(key, opts.headers[key]);
      });
    }
    xhr.send(opts.payload);
  });
};

/**
 * Map a videoclip
 */
export const mapVideoClip =
  ({ offset }) =>
  (videoClip) => {
    const startUnix = getDayjsFromSecomDate(videoClip.startDate).valueOf();
    const endUnix = getDayjsFromSecomDate(videoClip.endDate).valueOf();
    const startDate = dayjs(startUnix);
    const endDate = dayjs(endUnix);
    let hoursLong = false;
    let format = 'mm:ss';
    // Reformat duration for hour-long videos
    if (dayjs(endDate).diff(dayjs(startDate), 'hours') > 0) {
      hoursLong = true;
      format = 'HH:mm:ss';
    }

    const duration = dayjs.duration(endDate.diff(startDate));
    const durationFormat = duration.format(format);
    return {
      id: videoClip.id,
      thumbnailUrl: videoClip.thumbnailUrl,
      clipName: videoClip.name,
      startDate: startUnix,
      endDate: endUnix,
      durationMs: duration.asMilliseconds(),
      duration: durationFormat,
      event:
        videoClip.eventInfo &&
        videoClip.eventInfo.eventMetaDatas &&
        videoClip.eventInfo.eventMetaDatas[0]
          ? videoClip.eventInfo.eventMetaDatas[0].value === null
            ? 'イベント検知'
            : videoClip.eventInfo.eventMetaDatas[0].value
          : null,
      cameraId: videoClip.cameraId,
      createDate: getDayjsFromSecomDate(videoClip.createDate).valueOf(),
      status: videoClip.status,
      // items with a “status” not equal to “2” will be disabled for selection.
      disabled: videoClip.status !== 2,
      tags: videoClip.tags,
      offset: offset,
      hoursLong,
    };
  };

export const getFirmwareLatestVersion = (list) => {
  const mappedList = {
    [CameraFirmwareType.firmware]: null,
    [CameraFirmwareType.arobaViewRa]: null,
  };
  list.forEach((item) => {
    if (item.name === CameraFirmwareType.firmware) {
      const oldDate = +(
        mappedList[CameraFirmwareType.firmware]?.createDate || 0
      );

      if (!isNaN(oldDate) && oldDate < +item.createDate) {
        mappedList[CameraFirmwareType.firmware] = item;
      }
    } else if (item.name === CameraFirmwareType.arobaViewRa) {
      const oldDate = +(
        mappedList[CameraFirmwareType.arobaViewRa]?.createDate || 0
      );
      if (!isNaN(oldDate) && oldDate < +item.createDate) {
        mappedList[CameraFirmwareType.arobaViewRa] = item;
      }
    }
  });
  return mappedList;
};

export function asyncDebounce(func, wait) {
  const debounced = debounce(async (resolve, reject, bindSelf, args) => {
    try {
      const result = await func.bind(bindSelf)(...args);
      resolve(result);
    } catch (error) {
      reject(error);
    }
  }, wait);

  // This is the function that will be bound by the caller, so it must contain the `function` keyword.
  function returnFunc(...args) {
    return new Promise((resolve, reject) => {
      debounced(resolve, reject, this, args);
    });
  }

  return returnFunc;
}

export const checkFragLoadLevelError = async (data) => {
  if (data?.response?.code && [400, 403].includes(data.response.code)) {
    const fragError = await fetchFragStatus(data.response.url);

    return fragError;
  }
  return PlayerStatus.Uknown;
};

export const fetchFragStatus = async (url) => {
  try {
    await axios.get(url);
  } catch (error) {
    console.error(error.response);
    if (error?.response?.data?.Message) {
      if (
        (error.response.data.Message + '').includes('not have the same codec')
      ) {
        return PlayerStatus.CodecIsNotTheSame;
      }
      if (
        (error.response.data.Message + '').includes(
          'Streaming session token is expired.'
        )
      ) {
        return PlayerStatus.StreamingSessionExpired;
      }
    }
  }
  return PlayerStatus.Uknown;
};

export const getFiniteNumber = (input) => {
  if (isNaN(input)) {
    console.error('It is not a number ', input);
  }
  if (!isFinite(input)) {
    console.error('The number is infinite ', input);
  }
  return input;
};

export const getFullWidthNumericHyphenUnderbar = (input) => {
  // Step 1: Convert full-width alphanumerics (Ａ-Ｚ, ａ-ｚ, ０-９) to half-width.
  let result = input.replace(/[Ａ-Ｚａ-ｚ０-９]/g, (match) =>
    String.fromCharCode(match.charCodeAt(0) - 0xfee0)
  );

  // Step 2: Convert half-width katakana (Unicode range: \uFF66-\uFF9F) to full-width katakana.
  const halfWidthKatakanaMap = {
    ｱ: 'ア',
    ｲ: 'イ',
    ｳ: 'ウ',
    ｴ: 'エ',
    ｵ: 'オ',
    ｶ: 'カ',
    ｷ: 'キ',
    ｸ: 'ク',
    ｹ: 'ケ',
    ｺ: 'コ',
    ｻ: 'サ',
    ｼ: 'シ',
    ｽ: 'ス',
    ｾ: 'セ',
    ｿ: 'ソ',
    ﾀ: 'タ',
    ﾁ: 'チ',
    ﾂ: 'ツ',
    ﾃ: 'テ',
    ﾄ: 'ト',
    ﾅ: 'ナ',
    ﾆ: 'ニ',
    ﾇ: 'ヌ',
    ﾈ: 'ネ',
    ﾉ: 'ノ',
    ﾊ: 'ハ',
    ﾋ: 'ヒ',
    ﾌ: 'フ',
    ﾍ: 'ヘ',
    ﾎ: 'ホ',
    ﾏ: 'マ',
    ﾐ: 'ミ',
    ﾑ: 'ム',
    ﾒ: 'メ',
    ﾓ: 'モ',
    ﾔ: 'ヤ',
    ﾕ: 'ユ',
    ﾖ: 'ヨ',
    ﾗ: 'ラ',
    ﾘ: 'リ',
    ﾙ: 'ル',
    ﾚ: 'レ',
    ﾛ: 'ロ',
    ﾜ: 'ワ',
    ｦ: 'ヲ',
    ﾝ: 'ン',
    ｧ: 'ァ',
    ｨ: 'ィ',
    ｩ: 'ゥ',
    ｪ: 'ェ',
    ｫ: 'ォ',
    ｯ: 'ッ',
    ｬ: 'ャ',
    ｭ: 'ュ',
    ｮ: 'ョ',
  };

  result = result
    .split('')
    .map((char) => halfWidthKatakanaMap[char] || char)
    .join('');

  // Step 3: Remove disallowed characters.
  // Allowed characters:
  // - Full-width hiragana: ぁ-ん
  // - Full-width katakana: ァ-ン and ー
  // - Kanji: 一-龠
  // - Half-width alphanumerics: a-z, A-Z, 0-9
  // - Allowed symbols: hyphen (-) and underscore (_)
  // - Spaces: \s
  result = result.replace(/[^ぁ-んァ-ンー一-龠a-zA-Z0-9\-_ \s]/g, '');

  return result;
};

/**
 * Validate clip name: length 1-100, allowed characters (※使用可能文字).
 * @param {string} input
 * @returns {{ isValid: boolean, error: string|null }}
 */
export const validateClipName = (input) => {
  if (!input || typeof input !== 'string') {
    return { isValid: false, error: 'Input is empty' };
  }
  const trimmed = input.trim();
  if (trimmed.length < 1) {
    return { isValid: false, error: 'Input is empty' };
  }
  if (INPUT_WHITESPACE_REGEX.test(input)) {
    return { isValid: false, error: 'Input contains whitespace' };
  }
  if (trimmed.length > 100) {
    return { isValid: false, error: 'Name must be 1 to 100 characters' };
  }
  if (!CLIP_NAME_ALLOWED_REGEX.test(trimmed)) {
    return { isValid: false, error: 'Invalid characters' };
  }
  return { isValid: true, error: null };
};

/**
 * Validate input name (camera name, camera group name, ...)
 * @param {*} input
 * @returns
 */
export const validateInputName = (input) => {
  // check for empty input
  if (!input) {
    return {
      isValid: false,
      error: 'Input is empty',
    };
  }

  // check for any type of whitespace character
  if (INPUT_WHITESPACE_REGEX.test(input)) {
    return {
      isValid: false,
      error: 'Input contains whitespace',
    };
  }

  return {
    isValid: true,
    error: null,
  };
};

// blob to base64
export const blobToBase64 = async (blob) => {
  return new Promise((resolve, reject) => {
    var reader = new FileReader();
    reader.onload = function (event) {
      resolve(event.target.result);
    };
    reader.onerror = function (error) {
      reject(error);
    };
    reader.readAsDataURL(blob);
  });
};

/**
 * Retry fn
 */
export const retry = async (
  fn,
  {
    retries,
    retryIntervalMs,
    noRetryFn = () => false,
    onError = () => {},
    signal,
  }
) => {
  // check if the operation has been aborted before starting the retry logic
  if (signal?.aborted) {
    throw new DOMException('Aborted', 'AbortError');
  }

  try {
    return await fn();
  } catch (error) {
    if (retries <= 0 || noRetryFn(error)) {
      throw error;
    }
    onError({ error, retries });

    // wait for the retry interval or until the signal is aborted
    await Promise.race([
      sleep(retryIntervalMs),
      new Promise((_, reject) => {
        if (signal) {
          signal.addEventListener('abort', () =>
            reject(new DOMException('Aborted', 'AbortError'))
          );
        }
      }),
    ]);

    // check if the operation has been aborted before retrying
    if (signal?.aborted) {
      throw new DOMException('Aborted', 'AbortError');
    }

    return retry(fn, {
      retries: retries - 1,
      retryIntervalMs,
      noRetryFn,
      onError,
      signal,
    });
  }
};

const sleep = (ms = 0) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Check whether should retry or not
 * @param {Error} error
 * @returns
 */
export const noRetryFnGetHLSStreamingSessionURL = (error) => {
  const excludeMessage =
    'The security token included in the request is expired';

  const isNoFragError = error?.message
    ? error.message === excludeMessage
    : false;
  return isNoFragError;
};

/**
 * Get maximum value from Array<object>
 * @param {*} arr
 * @param {*} key
 * @returns
 */
export const getMaxValueFromObjects = (arr, valueKey) => {
  if (arr.length === 0) return null;
  const values = arr
    .filter((val) => !!val)
    .map((obj) => obj[valueKey])
    .filter((value) => value !== undefined);

  return values.length > 0 ? Math.max(...values) : null;
};

/**
 * Convert hours to days
 * @param {*} hours
 * @returns
 */
export const convertHoursToDays = (hours) => {
  if (typeof hours !== 'number' || isNaN(hours)) {
    throw new Error('Input must be a valid number');
  }

  if (hours < 0) {
    throw new Error('Input must be a non-negative number');
  }

  const hoursPerDay = 24;
  return hours / hoursPerDay;
};

/**
 * Set video current time using value and unit
 */
export const setVideoTimeByValueUnit = (videoElement, value, unit) => {
  const currentTime = videoElement.currentTime;
  let currentTimeInSeconds = '00';
  let currentTimeInMs = '000';
  if (currentTime > 0) {
    const currentTimeString = `${currentTime}`;
    const currentTimeArray = currentTimeString.includes('.')
      ? `${currentTimeString}000`.split('.')
      : [currentTimeString, '000'];
    currentTimeInSeconds = currentTimeArray[0];
    currentTimeInMs = currentTimeArray[1].substring(0, 3);
  }
  const newCurrentTime = dayjs
    .duration({
      seconds: currentTimeInSeconds,
      milliseconds: currentTimeInMs,
    })
    .add(value, unit)
    .asMilliseconds();

  const calculatedTime = `${newCurrentTime / 1000}`;
  videoElement.currentTime = getFiniteNumber(calculatedTime);
  return getFiniteNumber(calculatedTime);
};

/**
 * Convert unix milliseconds to ISO8601
 * @param {*} unixMilliseconds
 * @returns
 */
export const convertToISO8601 = (unixMilliseconds) => {
  return new Date(unixMilliseconds).toISOString();
};

/**
 * Get HLS live stream `startTimestamp`
 **/
export const getHLSLiveStreamStartTimestamp = () => {
  return dayjs().subtract(500, 'milliseconds').valueOf();
};

/**
 * Merge events with overlap
 * @param {*} events
 * @returns
 */
export function mergeEvents(events) {
  if (!events.length) return [];

  // Sort events by startUnix time
  events.sort((a, b) => a.startUnix - b.startUnix);

  const mergedEvents = [{ ...events[0] }];

  for (let i = 1; i < events.length; i++) {
    const currentEvent = { ...events[i] };
    const lastMergedEvent = mergedEvents[mergedEvents.length - 1];

    // If the current event overlaps with the last merged event, merge them
    if (currentEvent.startUnix <= lastMergedEvent.endUnix) {
      lastMergedEvent.endUnix = Math.max(
        lastMergedEvent.endUnix,
        currentEvent.endUnix
      );
      lastMergedEvent.end = new Date(lastMergedEvent.endUnix).toISOString();
    } else {
      mergedEvents.push(currentEvent);
    }
  }

  return mergedEvents;
}

/**
 * Sorts a list of objects based on the order specified in a list of IDs.
 *
 * @param {number[]} ids - An array of IDs specifying the desired order.
 * @param {Object[]} objects - An array of objects, each containing an `id` property.
 * @param {Boolean} removeUnmatched - Determines how to handle objects not included in the `ids` array.
 *                                    If `true`, unmatched objects are excluded from the result.
 *                                    If `false`, unmatched objects are appended to the end of the sorted result.
 * @returns {Object[]} - A new array of objects sorted according to the order of IDs.
 *
 * @example
 * // input:
 * const ids = [0, 2, 32, 3];
 * const objects = [{ id: 32 }, { id: 2 }, { id: 3 }, { id: 0 }];
 *
 * // output:
 * sortObjectsByIds(ids, objects);
 * // Result: [{ id: 0 }, { id: 2 }, { id: 32 }, { id: 3 }]
 */
export function sortObjectsByIds(ids, objects, removeUnmatched = true) {
  // create a mapping of ids to their corresponding objects
  const idToObjectMap = objects.reduce((map, obj) => {
    map[obj.id] = obj;
    return map;
  }, {});

  // create the sorted array based on the ids order
  const sorted = ids.map((id) => idToObjectMap[id]).filter(Boolean);

  if (removeUnmatched) {
    // if flag is true, return only sorted objects
    return sorted;
  } else {
    // if flag is false, include unmatched objects at the end
    const unmatched = objects.filter((obj) => !ids.includes(obj.id));
    return [...sorted, ...unmatched];
  }
}
